// Copyright (C) 2022 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import React, { useEffect, useRef, useState } from 'react'
import { IonApp, IonRouterOutlet } from "@ionic/react"
import { IonReactRouter } from "@ionic/react-router"
import { LocalizedProvider, LocalizedProviderState } from "react-localized"
import AnonymousUser from './AnonymousUser'
import { client } from './GatewayClient'
import IUser from './IUser'
import { MainToolbar } from './MainToolbar'
import { SessionContext } from './useSession'
import { ThemeContext } from "./useTheme"
import './App.scss'
import { KnowledgeBasePanel } from './KnowledgeBasePanel'


interface RoutedAppProps {
  anonymous?: boolean
  applicationComponent?: React.FC<{children: React.ReactNode}>
  children?: React.ReactNode
  locales?: any
  logo?: string
  name: string
  noheader?: boolean
  ready: boolean
  mainMenuComponent?: React.FC
  mainToolbarComponent?: React.FC
  isUnsupportedDevice?: () => boolean
}


const RoutedApp: React.FC<RoutedAppProps> = ({
  anonymous,
  children,
  locales,
  logo,
  name,
  noheader,
  isUnsupportedDevice,
  ...props
}) => {
  const [language, setLanguage] = useState<string>("en")
  const navigationRef = useRef<HTMLIonNavElement>(null)

  useEffect(() => {
    console.log("Loading country names translations for ", language)
    const countries = require("i18n-iso-countries")
    countries.registerLocale(require(`i18n-iso-countries/langs/${language}.json`))
  }, [language])

  const Main: React.FC<{state: LocalizedProviderState}> = ({state}) => {
    const Application = props.applicationComponent || React.Fragment
    const [articleContent, setArticleContent] = useState<string | null>(null)
    const [articlePath, setArticlePath] = useState<string | null>(null)
    const [available, setAvailable] = useState<boolean>(true)
    const [booting, setBooting] = useState<boolean>(true)
    const classNames: string[] = []
    const [error, setError] = useState<boolean|string|null>(null)
    const [loading, setLoading] = useState<boolean>(true)
    const [ready, setReady] = useState<boolean>(false)
    const [user, setUser] = useState<IUser>(((!anonymous) ? null! : (new AnonymousUser())))
    const ctx = {
      articleContent: articleContent,
      articlePath: articlePath,
      available: available,
      booting: booting,
      error: error,
      loading: loading,
      locale: language,
      nav: navigationRef.current!,
      ready: ready,
      user: user || new AnonymousUser(),
      forceAuthenticate: () => {
        setUser(new AnonymousUser())
      },
      forceRefresh: () => {},
      isAuthenticated: () => { return !!user && user.isAuthenticated() },
      isUnsupportedDevice: (isUnsupportedDevice || (() => false)),
      setArticleContent: setArticleContent,
      setArticlePath: setArticlePath,
      setBooting: setBooting,
      setError: setError,
      setLanguage: setLanguage,
      setLoading: setLoading,
      setServiceNotAvailable: (isNotAvailable: boolean) => setAvailable(!isNotAvailable),
      setUser: setUser
    }
    const theme = {
      name: name,
      logo: logo
    }

    // Clear article content if the path is null
    useEffect(() => {
      if (articlePath === null) {
        setArticleContent(null)
      }
    }, [articlePath])

    useEffect(() => {
      if (user === null && !anonymous) {
        client.getCurrentUser().then((user) => {
          setUser(user)
        })
      }
    }, [user])

    useEffect(() => {
      if (user !== null) {
        setReady(state.localeReady)
      }
    }, [user, state.localeReady])

    useEffect(() => {
      if (!!ready && props.ready) {
        setBooting(false)
      }
    }, [ready])

    const MainToolbarComponent: React.FC = props.mainToolbarComponent || MainToolbar
    if (!!noheader) {
      classNames.push("noheader")
    }
    return (
      <IonApp className={classNames.join(" ")}>
        <SessionContext.Provider value={ctx}>
          <ThemeContext.Provider value={theme}>
            <Application>
              {(!!noheader) ? null : (<MainToolbarComponent/>)}
              <IonReactRouter>
                {(!!props.mainMenuComponent) ? (<props.mainMenuComponent/>) : null}
                <IonRouterOutlet animated={false} id="main-content">
                {children}
                </IonRouterOutlet>
              </IonReactRouter>
            </Application>
          </ThemeContext.Provider>
        </SessionContext.Provider>
      </IonApp>
    )
  }

  return (
    <LocalizedProvider locales={locales || {}} selected={language}>
      {(state: any) => (<Main state={state}></Main>)}
    </LocalizedProvider>
  )
}

export { RoutedApp }